import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
  domtools,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import { renderMarkdown } from './dotglobal-markdown.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

type TIssueFilter = 'open' | 'closed' | 'all';

/**
 * Issues view: read-only issue list (open/closed + label filters, agent CTA) and a single
 * issue detail (comment timeline + labels/participants sidebar). Mirrors the Claude Design
 * `issues.jsx`, wired to the real public issue API. Extracted out of dotglobal-app. Emits
 * `dotglobal-navigate` and `dotglobal-propose` (open the propose modal).
 */
@customElement('dotglobal-issues')
export class DotglobalIssues extends DeesElement {
  @property({ type: Object })
  public accessor data: interfaces.IReq_PublicRepo['response'] | null = null;

  @property({ type: Number })
  public accessor selectedIssueNumber = 0;

  @state()
  private accessor issueFilter: TIssueFilter = 'open';

  @state()
  private accessor issueLabelFilter = '';

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }
      .empty { padding: 48px 20px; text-align: center; color: var(--text-muted); }
      .meta-item { display: inline-flex; align-items: center; gap: 6px; }

      .agent-cta {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 16px 18px;
        border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--border-default));
        border-radius: 8px;
        background: color-mix(in srgb, var(--accent) 7%, var(--bg-primary));
      }

      .listcard {
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        overflow: hidden;
      }
      .listhead {
        min-height: 48px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0 16px;
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-tertiary);
        font-size: 13px;
      }
      .listrow {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--border-subtle);
        color: inherit;
      }
      .listrow:last-child { border-bottom: 0; }
      .listrow:hover { background: var(--row-hover); }
      .listrow .it { flex-shrink: 0; margin-top: 1px; color: var(--text-muted); }
      .listrow .ti { color: var(--text-primary); font-size: 14.5px; font-weight: 600; }
      .listrow:hover .ti { color: var(--accent); }
      .listrow .sub { margin-top: 3px; color: var(--text-muted); font-size: 12px; }

      .filterbar { display: flex; align-items: center; gap: 8px; }
      .fpill {
        height: 30px;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 0 11px;
        border-radius: 6px;
        color: var(--text-secondary);
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
      }
      .fpill:hover { color: var(--text-primary); background: var(--hover); }
      .fpill.on { color: var(--text-primary); }

      .label {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border-radius: 999px;
        padding: 2px 8px;
        font-size: 11px;
        font-weight: 700;
      }
      .label-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

      .timeline { position: relative; }
      .timeline.solo .tl-item::after { display: none; }
      .tl-item { position: relative; display: flex; align-items: flex-start; gap: 14px; }
      .tl-item + .tl-item { margin-top: 16px; }
      .tl-item::after {
        content: '';
        position: absolute;
        left: 15px;
        top: 34px;
        bottom: -16px;
        width: 2px;
        background: var(--border-default);
      }
      .tl-item:last-child::after { display: none; }
      .tl-dot {
        position: relative;
        z-index: 1;
        flex-shrink: 0;
        border-radius: 50%;
        box-shadow: 0 0 0 4px var(--bg-primary);
      }
      .tl-dot .avatar { width: 30px; height: 30px; }
      .thread-item {
        position: relative;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        border: 1px solid var(--border-default);
        border-radius: 8px;
      }
      .thread-head {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 14px;
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-tertiary);
        font-size: 13px;
        flex-wrap: wrap;
      }
      .thread-body { padding: 16px; }
      .issue-actions {
        padding: 16px;
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
      }
      .action-error {
        color: var(--danger, #dc2626);
        font-size: 12px;
        overflow-wrap: anywhere;
      }
    `,
  ];

  @state()
  private accessor accessToken = '';

  @state()
  private accessor newIssueOpen = false;

  @state()
  private accessor newIssueTitle = '';

  @state()
  private accessor newIssueBody = '';

  @state()
  private accessor commentBody = '';

  @state()
  private accessor actionPending = false;

  @state()
  private accessor actionError = '';

  public render(): TemplateResult {
    const data = this.data;
    const repo = data?.repo;
    if (!data || !repo) {
      return html`<div class="page"><div class="empty">Repository not found.</div></div>`;
    }
    if (this.selectedIssueNumber) {
      const selectedIssue = data.selectedIssue ?? data.issues.find((issue) => issue.number === this.selectedIssueNumber);
      if (!selectedIssue) {
        return html`
          <div class="page">
            <button class="btn sm ghost" style="margin-bottom: 14px" @click=${() => this.navigate(this.issuesHash(repo))}>${this.icon('ChevronLeft')} Back to issues</button>
            <div class="empty">Issue #${this.selectedIssueNumber} was not returned by the public API.</div>
          </div>
        `;
      }
      return this.renderIssueDetail(data, selectedIssue);
    }
    return this.renderIssueList(data);
  }

  private renderIssueList(dataArg: interfaces.IReq_PublicRepo['response']): TemplateResult {
    const openIssues = dataArg.issues.filter((issue) => issue.state !== 'closed');
    const closedIssues = dataArg.issues.filter((issue) => issue.state === 'closed');
    const labels = Array.from(new Set(dataArg.issues.flatMap((issue) => issue.labels.map((label) => label.name)))).filter(Boolean).slice(0, 10);
    const visibleIssues = dataArg.issues
      .filter((issue) => {
        if (this.issueFilter === 'open') {
          return issue.state !== 'closed';
        }
        if (this.issueFilter === 'closed') {
          return issue.state === 'closed';
        }
        return true;
      })
      .filter((issue) => !this.issueLabelFilter || issue.labels.some((label) => label.name === this.issueLabelFilter));
    return html`
      <div class="page">
        <div class="agent-cta" style="margin-bottom: 18px">
          <div class="agent-glyph">${this.icon('CircleDot')}</div>
          <div class="grow">
            <div style="font-weight: 600; font-size: 13.5px">Repository issue tracker</div>
            <div class="muted" style="font-size: 12.5px; margin-top: 2px">Create and discuss issues with an organization access token.</div>
          </div>
          <button class="btn sm primary" @click=${() => { this.newIssueOpen = !this.newIssueOpen; this.actionError = ''; }}>${this.icon(this.newIssueOpen ? 'X' : 'Plus')} ${this.newIssueOpen ? 'Cancel' : 'New issue'}</button>
        </div>
        ${this.newIssueOpen ? this.renderNewIssueForm(dataArg.repo!) : ''}
        <div class="listcard">
          <div class="listhead">
            <div class="filterbar">
              <button class=${`fpill ${this.issueFilter === 'open' ? 'on' : ''}`} @click=${() => { this.issueFilter = 'open'; }}>${this.icon('CircleDot')} ${openIssues.length} Open</button>
              <button class=${`fpill ${this.issueFilter === 'closed' ? 'on' : ''}`} @click=${() => { this.issueFilter = 'closed'; }}>${this.icon('CircleCheck')} ${closedIssues.length} Closed</button>
            </div>
            <span class="grow"></span>
            ${labels.length
              ? html`<div class="row" style="gap: 8px">${labels.slice(0, 5).map((label) => html`<button style=${`opacity:${this.issueLabelFilter && this.issueLabelFilter !== label ? 0.4 : 1}`} @click=${() => { this.issueLabelFilter = this.issueLabelFilter === label ? '' : label; }}>${this.renderIssueLabel({ name: label, color: dataArg.issues.flatMap((issue) => issue.labels).find((item) => item.name === label)?.color || '666666' })}</button>`)}</div>`
              : ''}
          </div>
          ${visibleIssues.length
            ? visibleIssues.map((issue) => this.renderIssueRow(dataArg.repo!, issue))
            : html`<div class="empty">No issues match this filter.</div>`}
        </div>
      </div>
    `;
  }

  private renderIssueDetail(
    dataArg: interfaces.IReq_PublicRepo['response'],
    issueArg: interfaces.IPublicIssue,
  ): TemplateResult {
    const repo = dataArg.repo!;
    const participants = [issueArg.user, ...dataArg.selectedIssueComments.map((comment) => comment.user)]
      .filter((user) => user.login)
      .filter((user, index, users) => users.findIndex((otherUser) => otherUser.login === user.login) === index)
      .slice(0, 5);
    return html`
      <div class="page">
        <button class="btn sm ghost" style="margin-bottom: 14px" @click=${() => this.navigate(this.issuesHash(repo))}>${this.icon('ChevronLeft')} Back to issues</button>
        <div style="border-bottom: 1px solid var(--border-default); padding-bottom: 16px; margin-bottom: 20px">
          <div class="row" style="gap: 10px; align-items: baseline">
            <h1 style="margin: 0; font-size: 24px; line-height: 1.2; letter-spacing: -0.02em">${issueArg.title} <span class="muted" style="font-weight: 400">#${issueArg.number}</span></h1>
          </div>
          <div class="row" style="gap: 10px; margin-top: 12px; align-items: center; row-gap: 8px">
            <span class="badge sq" style=${`padding: 5px 12px; font-size: 12.5px; background:${issueArg.state === 'closed' ? '#8b5cf6' : 'var(--success)'}; color:#fff`}>
              ${this.icon(issueArg.state === 'closed' ? 'CircleCheck' : 'CircleDot')} ${issueArg.state === 'closed' ? 'Closed' : 'Open'}
            </span>
            <span class="muted" style="font-size: 13px"><b style="color: var(--text-primary)">${issueArg.user.login || 'unknown'}</b> opened this · ${issueArg.comments} comments</span>
            <span class="grow"></span>
            ${issueArg.labels.map((label) => this.renderIssueLabel(label))}
          </div>
        </div>

        <div class="row" style="align-items: flex-start; gap: 24px">
          <div class="grow stack" style="gap: 18px; min-width: 0">
            <div class="timeline solo">
              ${this.renderIssueComment(issueArg.user, issueArg.createdAt, issueArg.body || 'No issue description provided.', 'author')}
            </div>
            ${dataArg.selectedIssueComments.length
              ? html`<div class="timeline">${dataArg.selectedIssueComments.map((comment) => this.renderIssueComment(comment.user, comment.createdAt, comment.body))}</div>`
              : html`<div class="muted" style="font-size: 12.5px; margin-left: 44px">No public comments returned for this issue.</div>`}
            ${this.renderCommentForm(repo, issueArg)}
          </div>
          <div class="stack" style="width: 220px; flex-shrink: 0; gap: 16px; font-size: 12.5px">
            <div class="stack" style="gap: 8px">
              <div class="muted" style="font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em">Labels</div>
              <div class="row" style="gap: 6px">${issueArg.labels.map((label) => this.renderIssueLabel(label))}</div>
            </div>
            <div class="divider"></div>
            <div class="stack" style="gap: 8px">
              <div class="muted" style="font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em">Participants</div>
              <div class="row" style="gap: 6px">${participants.map((participant) => this.renderAvatar(participant.login, participant.avatarUrl))}</div>
            </div>
            <div class="divider"></div>
            <div class="stack" style="gap: 8px">
              <div class="fld">
                <label>Access token</label>
                <input
                  type="password"
                  autocomplete="off"
                  placeholder="fgu_…"
                  .value=${this.accessToken}
                  @input=${(eventArg: Event) => { this.accessToken = (eventArg.currentTarget as HTMLInputElement).value; }}
                />
              </div>
              <button
                class="btn sm"
                ?disabled=${this.actionPending || this.accessToken.length < 32}
                @click=${() => this.updateIssueState(repo, issueArg)}
              >${this.icon(issueArg.state === 'closed' ? 'RotateCcw' : 'CircleCheck')} ${issueArg.state === 'closed' ? 'Reopen issue' : 'Close issue'}</button>
              <button class="btn sm primary" @click=${() => this.propose()}>${this.icon('Sparkles')} Propose a change</button>
              ${this.actionError ? html`<div class="action-error">${this.actionError}</div>` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderNewIssueForm(repoArg: interfaces.IPublicRepo): TemplateResult {
    return html`
      <div class="issue-actions stack" style="gap: 12px; margin-bottom: 18px">
        <div class="fld">
          <label>Title</label>
          <input
            maxlength="256"
            placeholder="What needs attention?"
            .value=${this.newIssueTitle}
            @input=${(eventArg: Event) => { this.newIssueTitle = (eventArg.currentTarget as HTMLInputElement).value; }}
          />
        </div>
        <div class="fld">
          <label>Description</label>
          <textarea
            rows="6"
            maxlength="65536"
            placeholder="Describe the problem or proposal."
            .value=${this.newIssueBody}
            @input=${(eventArg: Event) => { this.newIssueBody = (eventArg.currentTarget as HTMLTextAreaElement).value; }}
          ></textarea>
        </div>
        <div class="fld">
          <label>Organization access token</label>
          <input
            type="password"
            autocomplete="off"
            placeholder="fgu_…"
            .value=${this.accessToken}
            @input=${(eventArg: Event) => { this.accessToken = (eventArg.currentTarget as HTMLInputElement).value; }}
          />
        </div>
        ${this.actionError ? html`<div class="action-error">${this.actionError}</div>` : ''}
        <div class="row" style="justify-content: flex-end">
          <button
            class="btn sm primary"
            ?disabled=${this.actionPending || !this.newIssueTitle.trim() || this.accessToken.length < 32}
            @click=${() => this.createIssue(repoArg)}
          >${this.icon('Plus')} ${this.actionPending ? 'Creating…' : 'Create issue'}</button>
        </div>
      </div>
    `;
  }

  private renderCommentForm(
    repoArg: interfaces.IPublicRepo,
    issueArg: interfaces.IPublicIssue,
  ): TemplateResult {
    return html`
      <div class="issue-actions stack" style="gap: 10px; margin-left: 44px">
        <div class="fld">
          <label>Add comment</label>
          <textarea
            rows="4"
            maxlength="65536"
            placeholder="Write a comment."
            .value=${this.commentBody}
            @input=${(eventArg: Event) => { this.commentBody = (eventArg.currentTarget as HTMLTextAreaElement).value; }}
          ></textarea>
        </div>
        <div class="row" style="gap: 10px; align-items: flex-end">
          <div class="fld grow">
            <label>Organization access token</label>
            <input
              type="password"
              autocomplete="off"
              placeholder="fgu_…"
              .value=${this.accessToken}
              @input=${(eventArg: Event) => { this.accessToken = (eventArg.currentTarget as HTMLInputElement).value; }}
            />
          </div>
          <button
            class="btn sm primary"
            ?disabled=${this.actionPending || !this.commentBody.trim() || this.accessToken.length < 32}
            @click=${() => this.createComment(repoArg, issueArg)}
          >${this.icon('MessageSquare')} ${this.actionPending ? 'Posting…' : 'Comment'}</button>
        </div>
        ${this.actionError ? html`<div class="action-error">${this.actionError}</div>` : ''}
      </div>
    `;
  }

  private async createIssue(repoArg: interfaces.IPublicRepo): Promise<void> {
    if (this.actionPending || !this.newIssueTitle.trim() || this.accessToken.length < 32) return;
    this.actionPending = true;
    this.actionError = '';
    try {
      const request = new domtools.plugins.typedrequest.TypedRequest<
        interfaces.IReq_RepositoryIssueCreate
      >('/typedrequest', 'repositoryIssueCreate');
      const response = await request.fire({
        org: repoArg.org,
        repo: repoArg.name,
        title: this.newIssueTitle,
        body: this.newIssueBody,
        accessToken: this.accessToken,
      });
      this.newIssueTitle = '';
      this.newIssueBody = '';
      this.newIssueOpen = false;
      this.navigate(`${this.issuesHash(repoArg)}/${response.issue.number}`);
    } catch (error) {
      this.actionError = this.errorText(error);
    } finally {
      this.actionPending = false;
    }
  }

  private async createComment(
    repoArg: interfaces.IPublicRepo,
    issueArg: interfaces.IPublicIssue,
  ): Promise<void> {
    if (this.actionPending || !this.commentBody.trim() || this.accessToken.length < 32) return;
    this.actionPending = true;
    this.actionError = '';
    try {
      const request = new domtools.plugins.typedrequest.TypedRequest<
        interfaces.IReq_RepositoryIssueCommentCreate
      >('/typedrequest', 'repositoryIssueCommentCreate');
      const response = await request.fire({
        org: repoArg.org,
        repo: repoArg.name,
        issueNumber: issueArg.number,
        body: this.commentBody,
        accessToken: this.accessToken,
      });
      const updatedIssue = {
        ...issueArg,
        comments: issueArg.comments + 1,
        updatedAt: response.comment.updatedAt,
      };
      this.data = this.data
        ? {
            ...this.data,
            issues: this.data.issues.map((issue) => issue.number === issueArg.number ? updatedIssue : issue),
            selectedIssue: updatedIssue,
            selectedIssueComments: [...this.data.selectedIssueComments, response.comment],
          }
        : this.data;
      this.commentBody = '';
    } catch (error) {
      this.actionError = this.errorText(error);
    } finally {
      this.actionPending = false;
    }
  }

  private async updateIssueState(
    repoArg: interfaces.IPublicRepo,
    issueArg: interfaces.IPublicIssue,
  ): Promise<void> {
    if (this.actionPending || this.accessToken.length < 32) return;
    this.actionPending = true;
    this.actionError = '';
    try {
      const request = new domtools.plugins.typedrequest.TypedRequest<
        interfaces.IReq_RepositoryIssueStateUpdate
      >('/typedrequest', 'repositoryIssueStateUpdate');
      const response = await request.fire({
        org: repoArg.org,
        repo: repoArg.name,
        issueNumber: issueArg.number,
        state: issueArg.state === 'closed' ? 'open' : 'closed',
        accessToken: this.accessToken,
      });
      this.data = this.data
        ? {
            ...this.data,
            issues: this.data.issues.map((issue) => issue.number === issueArg.number ? response.issue : issue),
            selectedIssue: response.issue,
          }
        : this.data;
    } catch (error) {
      this.actionError = this.errorText(error);
    } finally {
      this.actionPending = false;
    }
  }

  private renderIssueComment(
    userArg: interfaces.IPublicUser,
    createdAtArg: string,
    bodyArg: string,
    badgeArg = '',
  ): TemplateResult {
    return html`
      <div class="tl-item">
        <div class="tl-dot">${this.renderAvatar(userArg.login || 'unknown', userArg.avatarUrl)}</div>
        <div class="thread-item">
          <div class="thread-head">
            <b style="font-size: 13px; white-space: nowrap">${userArg.login || 'unknown'}</b>
            ${badgeArg ? html`<span class="badge outline sq" style="font-size: 10.5px">${badgeArg}</span>` : ''}
            <span class="muted" style="font-size: 12px; white-space: nowrap">commented ${this.timeAgo(createdAtArg)}</span>
          </div>
          <div class="thread-body">
            <div class="md">${renderMarkdown(bodyArg, 120)}</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderIssueRow(repoArg: interfaces.IPublicRepo, issueArg: interfaces.IPublicIssue): TemplateResult {
    const issueHash = `${this.issuesHash(repoArg)}/${issueArg.number}`;
    const isClosed = issueArg.state === 'closed';
    return html`
      <a class="listrow" href=${issueHash} @click=${(eventArg: Event) => { eventArg.preventDefault(); this.navigate(issueHash); }}>
        <span class="it" style="color: var(--success)">${this.icon(isClosed ? 'CircleCheck' : 'CircleDot')}</span>
        <div style="min-width: 0; flex: 1">
          <div class="row" style="gap: 8px">
            <span class="ti">${issueArg.title}</span>
            ${issueArg.labels.map((label) => this.renderIssueLabel(label))}
          </div>
          <div class="sub row" style="gap: 6px">
            <span>#${issueArg.number}</span><span>·</span><span>opened ${this.timeAgo(issueArg.createdAt)}</span><span>·</span><span>by ${issueArg.user.login || 'unknown'}</span>
          </div>
        </div>
        ${issueArg.comments > 0 ? html`<div class="meta-item muted" style="flex-shrink: 0; font-size: 12.5px">${this.icon('MessageSquare')} ${issueArg.comments}</div>` : ''}
      </a>
    `;
  }

  private renderIssueLabel(labelArg: interfaces.IPublicIssueLabel): TemplateResult {
    const color = `#${labelArg.color || '666666'}`;
    return html`
      <span class="label" style=${`color:${color}; border:1px solid color-mix(in srgb, ${color} 45%, transparent); background:color-mix(in srgb, ${color} 12%, transparent)`}>
        <span class="label-dot" style=${`background:${color}`}></span>${labelArg.name}
      </span>
    `;
  }

  private issuesHash(repoArg: interfaces.IPublicRepo): string {
    return `#/repo/${encodeURIComponent(repoArg.org)}/${encodeURIComponent(repoArg.name)}/issues`;
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }

  private propose(): void {
    this.dispatchEvent(new CustomEvent('dotglobal-propose', { bubbles: true, composed: true }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${15}></dees-icon>`;
  }

  private renderAvatar(labelArg: string, avatarUrlArg: string): TemplateResult {
    const initials = (labelArg || 'fg').split(/[.\s/-]+/).filter(Boolean).slice(0, 2).map((partArg) => partArg[0]?.toUpperCase()).join('') || 'fg';
    return html`
      <span class="avatar">
        ${initials}
        ${avatarUrlArg
          ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
          : ''}
      </span>
    `;
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return 'unknown';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    return `${Math.round(days / 30)}mo ago`;
  }

  private errorText(errorArg: unknown): string {
    return errorArg instanceof Error && errorArg.message
      ? errorArg.message.slice(0, 500)
      : 'Issue request failed';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-issues': DotglobalIssues;
  }
}
